"use client";

import axios from "axios";
import { useEffect, useState } from "react";
import { FcGoogle } from "react-icons/fc";
import { useRouter, useSearchParams } from "next/navigation";
import { toast } from "react-hot-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { RegisterSchema } from "../schemas";
import { Input } from "../../components/ui/input";
import { ButtonS } from "../../components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../components/ui/form";
import Heading from "../components/Heading";
import { SafeUser } from "../types";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Container from "../components/Container";
import { signIn } from "next-auth/react";
import { Checkbox } from "@/components/ui/checkbox";
import ReCAPTCHA from "react-google-recaptcha";
import Button from "../components/Button";
import { FiGithub } from "react-icons/fi";

interface RegisterSelectProps {
  currentUser?: SafeUser | null;
}

const Register: React.FC<RegisterSelectProps> = ({ currentUser }) => {
  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | undefined>("");
  const [success, setSuccess] = useState<string | undefined>("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);

  const searchParams = useSearchParams();
  const urlError =
    searchParams?.get("error") === "OAuthAccountNotLinked"
      ? "Email already in use with a different provider!"
      : "";

  const form = useForm<z.infer<typeof RegisterSchema>>({
    resolver: zodResolver(RegisterSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      agreeToTerms: false,
      recaptchaToken: "",
    },
  });

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setEmail(email);
  };

  // MEJORADO: Handler OAuth con mejor manejo de errores
  const handleOAuthSignIn = async (provider: "google" | "github") => {
    if (currentUser) {
      toast.error("Please log out first");
      return;
    }

    try {
      setIsLoading(true);
      
      // Construir callbackUrl con parámetros de plan si existen
      const plan = searchParams?.get("plan");
      const yearly = searchParams?.get("yearly");
      
      let callbackUrl = "/";
      if (plan) {
        const params = new URLSearchParams();
        params.set("plan", plan);
        if (yearly) params.set("yearly", yearly);
        callbackUrl = `/?${params.toString()}`;
      }

      const result = await signIn(provider, {
        callbackUrl,
        redirect: false,
      });

      // Si hay error y no redirige
      if (result?.error) {
        console.error(`${provider} sign in error:`, result.error);
        toast.error(`Failed to sign in with ${provider}. Please try again.`);
        setIsLoading(false);
      }
    } catch (error) {
      console.error(`${provider} sign in error:`, error);
      toast.error(`An error occurred with ${provider} sign in`);
      setIsLoading(false);
    }
  };

  const onSubmit = async (values: z.infer<typeof RegisterSchema>) => {
    if (currentUser) {
      toast.error("Please log out first");
      return;
    }

    if (emailError) {
      toast.error("This email is already in use.");
      return;
    }

    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const finalValues = {
        ...values,
        name: values.name,
      };

      // Registrar usuario
      const response = await axios.post("/api/register", { ...finalValues });
      const userId = response.data.id;

      // Esperar un poco antes del auto-login
      await new Promise(resolve => setTimeout(resolve, 500));

      // Hacer el signIn
      const signInResponse = await signIn("credentials", {
        redirect: false,
        email: values.email,
        password: values.password,
      });

      if (signInResponse?.ok && !signInResponse?.error) {
        toast.success("Welcome! You are now logged in.");

        const plan = searchParams?.get("plan");
        const yearly = searchParams?.get("yearly") === "true";

        if (plan) {
          const planKey = `${plan}${yearly ? "-yearly" : "-monthly"}`;
          try {
            const stripeResponse = await axios.post("/api/stripe/subscribe", {
              plan: planKey,
            });
            
            if (stripeResponse.data.url) {
              toast.success("Redirecting to payment page...");
              await new Promise(resolve => setTimeout(resolve, 1000));
              window.location.href = stripeResponse.data.url;
            } else {
              toast.success(stripeResponse.data.message);
              router.push(`/dashboard/${userId}`);
            }
          } catch (stripeError) {
            console.error("Stripe subscription error:", stripeError);
            toast.error("Payment setup failed, but registration was successful.");
            router.push(`/dashboard/${userId}`);
          }
        } else {
          router.push(`/dashboard/${userId}`);
        }
      } else {
        console.error("Auto-login failed:", signInResponse?.error);
        toast.success("Registration successful! Please log in.");
        router.push("/login");
      }
    } catch (error) {
      console.error("Registration error:", error);
      if (axios.isAxiosError(error)) {
        const errorMessage =
          error.response?.data?.message ||
          "Registration failed. Please try again.";
        toast.error(errorMessage);
        setError(errorMessage);
      } else {
        toast.error("Registration failed. Please try again.");
        setError("Registration failed.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (urlError) {
      toast.error(urlError);
    }
  }, [urlError]);

  // Verificar si ya está autenticado
  useEffect(() => {
    if (currentUser) {
      router.push("/");
    }
  }, [currentUser, router]);

  return (
    <div className="flex flex-col gap-2 text-black py-8">
      <Container>
        <Heading title="Register" subtitle="Create your account below" />
        
        <div className="text-neutral-500 text-center my-4 text-sm gap-4">
          <Button
            outline
            label="Continue with Google"
            icon={FcGoogle}
            onClick={() => handleOAuthSignIn("google")}
            disabled={isLoading}
          />
          <div className="mt-2"></div>
          <Button
            outline
            label="Continue with Github"
            icon={FiGithub}
            onClick={() => handleOAuthSignIn("github")}
            disabled={isLoading}
          />
        </div>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-white px-2 text-gray-500">Or continue with email</span>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4 mt-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-900">Name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Name"
                        disabled={isLoading}
                        className="bg-white border-gray-400 text-black placeholder-gray-500"
                      />
                    </FormControl>
                    <FormMessage className="text-red-500 text-xs" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-900">Email</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={email}
                        onChange={(e) => {
                          field.onChange(e);
                          handleEmailChange(e);
                        }}
                        placeholder="example@example.com"
                        type="email"
                        disabled={isLoading}
                        className="bg-white border-gray-400 text-black placeholder-gray-500"
                      />
                    </FormControl>
                    <FormMessage className="text-red-500 text-xs" />
                    {emailError && (
                      <FormMessage className="text-blue-500 text-sm">
                        {emailError}
                      </FormMessage>
                    )}
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-900">Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          {...field}
                          placeholder="******"
                          type={showPassword ? "text" : "password"}
                          disabled={isLoading}
                          className="bg-white border-gray-400 text-black placeholder-gray-500 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((prev) => !prev)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                          disabled={isLoading}
                        >
                          {showPassword ? (
                            <FaEyeSlash size={16} />
                          ) : (
                            <FaEye size={16} />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage className="text-red-500 text-xs" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="agreeToTerms"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={isLoading}
                        className="border-2 border-gray-900 rounded-sm focus:ring-0 focus:ring-offset-0"
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-xs">
                        I have read and agree to the{" "}
                        <a
                          href="/privacy-policy"
                          target="_blank"
                          className="text-emerald-600 hover:text-emerald-500"
                        >
                          Privacy Policy
                        </a>{" "}
                        and{" "}
                        <a
                          href="/terms-of-service"
                          target="_blank"
                          className="text-emerald-600 hover:text-emerald-500"
                        >
                          Terms of Service
                        </a>
                        .
                      </FormLabel>
                      <FormMessage className="text-red-500 text-xs" />
                    </div>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="recaptchaToken"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      {recaptchaSiteKey ? (
                        <ReCAPTCHA
                          sitekey={recaptchaSiteKey}
                          onChange={(value) => field.onChange(value)}
                        />
                      ) : (
                        <div className="text-red-500 text-xs">
                          ReCAPTCHA is not configured. Missing `NEXT_PUBLIC_RECAPTCHA_SITE_KEY`.
                        </div>
                      )}
                    </FormControl>
                    <FormMessage className="text-red-500 text-xs" />
                  </FormItem>
                )}
              />
            </div>
            {error && <div className="text-red-500 text-xs">{error}</div>}
            {success && <div className="text-green-500 text-xs">{success}</div>}
            <ButtonS
              type="submit"
              disabled={isLoading}
              className="h-10 w-full rounded-lg bg-emerald-600 text-white font-semibold text-md py-3 hover:bg-emerald-500 transition disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isLoading ? "Creating account..." : "Create Account"}
            </ButtonS>
          </form>
        </Form>

        <div className="text-neutral-500 text-center my-4 text-sm gap-4">
          <p className="mt-4">
            Have an account?
            <span
              onClick={() => {
                if (!isLoading) {
                  router.push("/login");
                }
              }}
              className="text-emerald-600 cursor-pointer hover:underline"
            >
              {" "}
              Login
            </span>
          </p>
        </div>
      </Container>
    </div>
  );
};

export default Register;

// "use client";

// import axios from "axios";
// import { useEffect, useState } from "react";
// import { FcGoogle } from "react-icons/fc";
// import { useRouter, useSearchParams } from "next/navigation";
// import { toast } from "react-hot-toast";
// import { useForm } from "react-hook-form";
// import { zodResolver } from "@hookform/resolvers/zod";
// import * as z from "zod";
// import { RegisterSchema } from "../schemas";
// import { Input } from "../../components/ui/input";
// import { ButtonS } from "../../components/ui/button";
// import {
//   Form,
//   FormControl,
//   FormField,
//   FormItem,
//   FormLabel,
//   FormMessage,
// } from "../../components/ui/form";
// import Heading from "../components/Heading";
// import { SafeUser } from "../types";
// import { FaEye, FaEyeSlash } from "react-icons/fa";
// import Container from "../components/Container";
// import { signIn } from "next-auth/react";
// import { Checkbox } from "@/components/ui/checkbox";
// import ReCAPTCHA from "react-google-recaptcha";
// import Button from "../components/Button";
// import { FiGithub } from "react-icons/fi";

// interface RegisterSelectProps {
//   currentUser?: SafeUser | null;
// }

// const Register: React.FC<RegisterSelectProps> = ({
//   currentUser,
// }) => {
//   const router = useRouter();
//   const [isLoading, setIsLoading] = useState(false);
//   const [showPassword, setShowPassword] = useState(false);
//   const [error, setError] = useState<string | undefined>("");
//   const [success, setSuccess] = useState<string | undefined>("");
//   const [email, setEmail] = useState("");
//   const [emailError, setEmailError] = useState<string | null>(null);

//   const searchParams = useSearchParams();
//   const urlError =
//     searchParams?.get("error") === "OAuthAccountNotLinked"
//       ? "Email already in use with a different provider!"
//       : "";

//   const form = useForm<z.infer<typeof RegisterSchema>>({
//     resolver: zodResolver(RegisterSchema),
//     defaultValues: {
//       name: "",
//       email: "",
//       password: "",
//       agreeToTerms: false,
//       recaptchaToken: "",
//     },
//   });

//   const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
//     const email = e.target.value;
//     setEmail(email);
//   };

//   // FIXED: Handler para OAuth con callbackUrl correcto
//   const handleOAuthSignIn = async (provider: "google" | "github") => {
//     try {
//       // Redirigir a la raíz después del login OAuth
//       await signIn(provider, { 
//         callbackUrl: "/" 
//       });
//     } catch (error) {
//       console.error(`${provider} sign in error:`, error);
//       toast.error(`Failed to sign in with ${provider}`);
//     }
//   };

//   const onSubmit = async (values: z.infer<typeof RegisterSchema>) => {
//     if (currentUser) {
//       toast.error("Please log out first");
//       return;
//     }

//     if (emailError) {
//       toast.error("This email is already in use.");
//       return;
//     }

//     setIsLoading(true);
//     setError("");
//     setSuccess("");

//     try {
//       const finalValues = {
//         ...values,
//         name: values.name,
//       };

//       const response = await axios.post("/api/register", { ...finalValues });
//       const userId = response.data.id;

//       // Hacer el signIn y esperar a que se complete
//       const signInResponse = await signIn("credentials", {
//         redirect: false,
//         email: values.email,
//         password: values.password,
//       });

//       if (signInResponse?.ok && !signInResponse?.error) {
//         toast.success("Welcome! You are now logged in.");

//         const plan = searchParams?.get("plan");
//         const yearly = searchParams?.get("yearly") === "true";

//         if (plan) {
//           const planKey = `${plan}${yearly ? "-yearly" : "-monthly"}`;
//           try {
//             const stripeResponse = await axios.post("/api/stripe/subscribe", { plan: planKey });
//             if (stripeResponse.data.url) {
//               toast.success("Redirecting to payment page...");
//               setTimeout(() => {
//                 window.location.replace(stripeResponse.data.url);
//               }, 1000);
//             } else {
//               toast.success(stripeResponse.data.message);
//               router.replace(`/dashboard/${userId}`);
//             }
//           } catch (stripeError) {
//             console.error("Stripe subscription error:", stripeError);
//             toast.error("Payment setup failed, but registration was successful.");
//             router.replace(`/dashboard/${userId}`);
//           }
//         } else {
//           router.replace(`/dashboard/${userId}`);
//         }
//       } else {
//         console.error("Auto-login failed:", signInResponse?.error);
//         toast.success("Registration successful! Please log in.");
//         router.replace("/login");
//       }
//     } catch (error) {
//       console.error("Registration error:", error);
//       if (axios.isAxiosError(error)) {
//         const errorMessage = error.response?.data?.message || "Registration failed. Please try again.";
//         toast.error(errorMessage);
//         setError(errorMessage);
//       } else {
//         toast.error("Registration failed. Please try again.");
//         setError("Registration failed.");
//       }
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   useEffect(() => {
//     if (urlError) {
//       toast.error(urlError);
//     }
//   }, [urlError]);

//   return (
//     <div className="flex flex-col gap-2 text-black py-8">
//       <Container>
//         <Heading
//           title="Register"
//           subtitle="Create your account below"
//         />
//         <div className="text-neutral-500 text-center my-4 text-sm gap-4">
//           <Button
//             outline
//             label="Continue with Google"
//             icon={FcGoogle}
//             onClick={() => handleOAuthSignIn("google")}
//           />
//           <div className="mt-2"></div>
//           <Button
//             outline
//             label="Continue with Github"
//             icon={FiGithub}
//             onClick={() => handleOAuthSignIn("github")}
//           />
//         </div>
//         <Form {...form}>
//           <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
//             <div className="space-y-4 mt-2">
//               <FormField
//                 control={form.control}
//                 name="name"
//                 render={({ field }) => (
//                   <FormItem>
//                     <FormLabel className="text-gray-900">Name</FormLabel>
//                     <FormControl>
//                       <Input
//                         {...field}
//                         placeholder="Name"
//                         className="bg-white border-gray-400 text-black placeholder-gray-500"
//                       />
//                     </FormControl>
//                     <FormMessage className="text-red-500 text-xs" />
//                   </FormItem>
//                 )}
//               />
//               <FormField
//                 control={form.control}
//                 name="email"
//                 render={({ field }) => (
//                   <FormItem>
//                     <FormLabel className="text-gray-900">Email</FormLabel>
//                     <FormControl>
//                       <Input
//                         {...field}
//                         value={email}
//                         onChange={(e) => {
//                           field.onChange(e);
//                           handleEmailChange(e);
//                         }}
//                         placeholder="example@example.com"
//                         type="email"
//                         className="bg-white border-gray-400 text-black placeholder-gray-500"
//                       />
//                     </FormControl>
//                     <FormMessage className="text-red-500 text-xs" />
//                     {emailError && (
//                       <FormMessage className="text-blue-500 text-sm">
//                         {emailError}
//                       </FormMessage>
//                     )}
//                   </FormItem>
//                 )}
//               />
//               <FormField
//                 control={form.control}
//                 name="password"
//                 render={({ field }) => (
//                   <FormItem>
//                     <FormLabel className="text-gray-900">Password</FormLabel>
//                     <FormControl>
//                       <div className="relative">
//                         <Input
//                           {...field}
//                           placeholder="******"
//                           type={showPassword ? "text" : "password"}
//                           className="bg-white border-gray-400 text-black placeholder-gray-500 pr-10"
//                         />
//                         <button
//                           type="button"
//                           onClick={() => setShowPassword((prev) => !prev)}
//                           className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
//                         >
//                           {showPassword ? (
//                             <FaEyeSlash size={16} />
//                           ) : (
//                             <FaEye size={16} />
//                           )}
//                         </button>
//                       </div>
//                     </FormControl>
//                     <FormMessage className="text-red-500 text-xs" />
//                   </FormItem>
//                 )}
//               />
//               <FormField
//                 control={form.control}
//                 name="agreeToTerms"
//                 render={({ field }) => (
//                   <FormItem className="flex flex-row items-start space-x-3 space-y-0">
//                     <FormControl>
//                       <Checkbox
//                         checked={field.value}
//                         onCheckedChange={field.onChange}
//                         className="border-2 border-gray-900 rounded-sm focus:ring-0 focus:ring-offset-0"
//                       />
//                     </FormControl>
//                     <div className="space-y-1 leading-none">
//                       <FormLabel className="text-xs">
//                         I have read and agree to the{" "}
//                         <a
//                           href="/privacy-policy"
//                           target="_blank"
//                           className="text-emerald-600 hover:text-emerald-500"
//                         >
//                           Privacy Policy
//                         </a>{" "}
//                         and{" "}
//                         <a
//                           href="/terms-of-service"
//                           target="_blank"
//                           className="text-emerald-600 hover:text-emerald-500"
//                         >
//                           Terms of Service
//                         </a>
//                         .
//                       </FormLabel>
//                       <FormMessage className="text-red-500 text-xs" />
//                     </div>
//                   </FormItem>
//                 )}
//               />
//               <FormField
//                 control={form.control}
//                 name="recaptchaToken"
//                 render={({ field }) => (
//                   <FormItem>
//                     <FormControl>
//                       <ReCAPTCHA
//                         sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!}
//                         onChange={(value) => field.onChange(value)}
//                       />
//                     </FormControl>
//                     <FormMessage className="text-red-500 text-xs" />
//                   </FormItem>
//                 )}
//               />
//             </div>
//             {error && <div className="text-red-500 text-xs">{error}</div>}
//             {success && <div className="text-green-500 text-xs">{success}</div>}
//             <ButtonS
//               type="submit"
//               disabled={isLoading}
//               className="h-10 w-full rounded-lg bg-emerald-600 text-white font-semibold text-md py-3 hover:bg-emerald-500 transition disabled:opacity-70 disabled:cursor-not-allowed"
//             >
//               {isLoading ? "Creating account..." : "Create Account"}
//             </ButtonS>
//           </form>
//         </Form>

//         <div className="text-neutral-500 text-center my-4 text-sm gap-4">
//           <p className="mt-4">
//             Have an account?
//             <span
//               onClick={() => {
//                 router.push("/login");
//               }}
//               className="text-emerald-600 cursor-pointer hover:underline"
//             >
//               {" "}
//               Login
//             </span>
//           </p>
//         </div>
//       </Container>
//     </div>
//   );
// };

// export default Register;